import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@services/baseQuery';
import { setACCGTaxCode } from '@store/slices/accounting-and-finance/accg-tax-code.slice';
import {
  CASGetSIDetailsRoomPosting,
  CASTransactionList,
} from '../../interfaces/cas.interface';
import { ITaxCodeResponse } from '../../modules/front-office/interface/room-posting-interface';
import { handleError } from '../../utils/api-error-handling';
import { encryptPayload } from '../../utils/security';

export const casApi = createApi({
  reducerPath: 'casApi',
  baseQuery: baseQuery,
  tagTypes: ['CAS', 'TAXCODE', 'List'],
  endpoints: (builder) => ({
    postCASTransaction: builder.mutation({
      query: (payload) => ({
        url: 'cas',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['CAS'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    getCASTransaction: builder.query({
      query: ({ params, query }) => {
        const casType = params?.casType || 'NA';
        const queryString = query?.casID ? `?casID=${query.casID}` : '';

        return `cas/transactions/${casType}/${queryString}`;
      },
      providesTags: ['CAS'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: CASTransactionList) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),

    getCASSIDetails: builder.query({
      query: (params) =>
        `cas/si-details/${params.transType}/${params?.transID}`,
      providesTags: ['CAS'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: {
        data: { getGuestLedgerData: CASGetSIDetailsRoomPosting[] };
      }) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
    }),
    getTaxCode: builder.query({
      query: (query) => {
        return `cas/taxcode?${query}`;
      },
      providesTags: ['TAXCODE'],

      transformResponse: async (response: ITaxCodeResponse) => {
        return response?.data;
      },
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data: response } = await queryFulfilled;

          if (!response) return;

          dispatch(setACCGTaxCode(response));
        } catch (error) {
          console.error(error);
        }
      },
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      forceRefetch() {
        return true;
      },
    }),

    postCWTransaction: builder.mutation({
      query: (payload) => ({
        url: 'cas/cwt',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['CAS'],
      transformErrorResponse: async (baseQueryReturnValue) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: any) => {
        return response;
      },
    }),

    getCASListTransaction: builder.query({
      query: (query) => {
        return `cas/transactions/${query}`;
      },
      providesTags: ['List'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
      transformResponse: async (response: CASTransactionList) => {
        return response;
      },
      forceRefetch() {
        return true;
      },
      keepUnusedDataFor: 0.0001,
    }),
  }),
});

export const {
  usePostCASTransactionMutation,
  useGetCASTransactionQuery,
  useGetCASListTransactionQuery,
  useLazyGetCASTransactionQuery,
  useGetTaxCodeQuery,
  usePostCWTransactionMutation,
  useLazyGetCASSIDetailsQuery,
  useLazyGetCASListTransactionQuery,
} = casApi;
